export const LOGIN_REJECT_REASONS = {
  PENDING_AUDIT: 'pending_audit',
  REJECTED: 'rejected',
  TRIAL_EXPIRED: 'trial_expired',
}

const DEFAULT_CONFIG = {
  trial_days: 7,
  expire_remind_days: 3,
  register_success_title: '注册成功',
  register_success_content:
    '您的账号已注册成功，请耐心等待客服审核。审核通过后，您将获得 {trial_days} 天试用期。',
  register_success_qr: '',
  register_success_qr_tip: '扫描上方二维码联系客服',
  expire_remind_title: '试用期即将到期',
  expire_remind_content: '您的试用期还剩 {remain_days} 天，请及时联系客服续期。',
  expire_remind_qr: '',
  expire_remind_qr_tip: '扫描上方二维码联系客服',
  expired_login_title: '试用期已到期',
  expired_login_tip: '您的试用期已到期，请联系客服续期。',
  expired_login_qr: '',
  expired_login_qr_tip: '扫描上方二维码联系客服',
}

export function getTrialConfigFields(configData = {}) {
  return {
    ...DEFAULT_CONFIG,
    ...pickTrialFields(configData),
  }
}

function pickTrialFields(data) {
  const keys = Object.keys(DEFAULT_CONFIG)
  const result = {}
  keys.forEach((key) => {
    if (data[key] !== undefined && data[key] !== null && data[key] !== '') {
      result[key] = data[key]
    }
  })
  return result
}

export function formatTrialContent(content, vars = {}) {
  if (!content) return ''
  return String(content).replace(/\{(\w+)\}/g, (_, key) => {
    if (vars[key] !== undefined && vars[key] !== null) {
      return String(vars[key])
    }
    return `{${key}}`
  })
}

export function resolveImageUrl(path, baseUrl) {
  if (!path) return ''
  if (/^https?:\/\//.test(path)) return path
  const base = (baseUrl || '').replace(/\/$/, '')
  const normalized = path.startsWith('/') ? path : `/${path}`
  return `${base}${normalized}`
}

export function calcRemainDays(trialEndTime) {
  if (!trialEndTime) return null
  const end = new Date(String(trialEndTime).replace(/-/g, '/'))
  if (Number.isNaN(end.getTime())) return null
  end.setHours(23, 59, 59, 999)
  const now = new Date()
  now.setHours(0, 0, 0, 0)
  const diff = end.getTime() - now.getTime()
  return Math.ceil(diff / (1000 * 60 * 60 * 24))
}

export function formatTrialEndDate(trialEndTime) {
  if (!trialEndTime) return ''
  const str = String(trialEndTime)
  if (str.length >= 10) return str.slice(0, 10)
  return str
}

export function getAuditStatusText(auditStatus) {
  const map = {
    0: '审核中',
    1: '正常',
    2: '已拒绝',
  }
  return map[auditStatus] || '正常'
}

export function getTrialEndDisplay(trialEndTime, remainDays) {
  const date = formatTrialEndDate(trialEndTime)
  if (!date) return '--'
  if (remainDays != null && remainDays > 0) {
    return `${date}（剩余 ${remainDays} 天）`
  }
  if (remainDays != null && remainDays <= 0) {
    return `${date}（已到期）`
  }
  return date
}

export function shouldShowExpireRemind(remainDays, expireRemindDays) {
  if (remainDays == null || expireRemindDays == null) return false
  return remainDays > 0 && remainDays <= Number(expireRemindDays)
}

export function getRemindStorageKey(userId, pageKey = 'default') {
  const date = new Date().toISOString().slice(0, 10)
  return `trial_remind_${pageKey}_${userId}_${date}`
}

export function hasShownRemindToday(userId, pageKey = 'default') {
  if (!userId) return false
  return !!localStorage.getItem(getRemindStorageKey(userId, pageKey))
}

export function markRemindShown(userId, pageKey = 'default') {
  if (!userId) return
  localStorage.setItem(getRemindStorageKey(userId, pageKey), '1')
}

/**
 * 检查是否应展示试用到期提醒（同一用户同一页面同一自然日仅弹一次）
 * @returns {Promise<{trialConfig, remainDays, userId}|null>}
 */
export async function prepareTrialExpireRemind(api, pageKey = 'default') {
  const token = localStorage.getItem('token')
  if (!token) return null

  const trialConfig = await fetchTrialConfig(api)
  let userRes
  try {
    userRes = await api.index.UserIndex()
  } catch (e) {
    return null
  }
  if (!userRes || userRes.code != 200) return null

  const userData = userRes.data || {}
  const userId =
    userData.user_id || userData.id || localStorage.getItem('userId')
  if (userId) {
    localStorage.setItem('userId', String(userId))
  }

  const remainDays =
    userData.remain_days != null
      ? Number(userData.remain_days)
      : calcRemainDays(userData.trial_end_time)

  if (!shouldShowExpireRemind(remainDays, trialConfig.expire_remind_days)) {
    return null
  }
  if (hasShownRemindToday(userId, pageKey)) return null

  return { trialConfig, remainDays, userId }
}

export function saveUserSession(userData = {}) {
  if (userData.token) {
    localStorage.setItem('token', userData.token)
  }
  if (userData.name) {
    localStorage.setItem('userName', userData.name)
  }
  if (userData.pic !== undefined) {
    localStorage.setItem('userPic', userData.pic || '')
  }
  const userId = userData.user_id || userData.id
  if (userId) {
    localStorage.setItem('userId', String(userId))
  }
}

export function clearUserSession() {
  localStorage.removeItem('token')
  localStorage.removeItem('userName')
  localStorage.removeItem('userPic')
  localStorage.removeItem('userId')
}

export async function fetchTrialConfig(api) {
  try {
    const res = await api.index.getConfig()
    if (res && res.code == 200) {
      return getTrialConfigFields(res.data)
    }
  } catch (e) {
    // ignore
  }
  return getTrialConfigFields({})
}

export function resolveLoginReject(res = {}) {
  const data = res.data || {}
  const reason = data.reason || data.reject_reason
  if (reason) return reason
  if (data.audit_status === 0) return LOGIN_REJECT_REASONS.PENDING_AUDIT
  if (data.audit_status === 2) return LOGIN_REJECT_REASONS.REJECTED
  const remainDays =
    data.remain_days != null ? data.remain_days : calcRemainDays(data.trial_end_time)
  if (remainDays != null && remainDays <= 0 && data.audit_status === 1) {
    return LOGIN_REJECT_REASONS.TRIAL_EXPIRED
  }
  return null
}
